import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Map, Calendar, Users, Package, LogOut, Heart, ChevronLeft, Sparkles, FileSpreadsheet, MessageSquare, Crown, ClipboardList, Settings, Lock, Warehouse, FileText, Navigation, Archive, HelpCircle, Activity } from "lucide-react";
import { usePermissions } from "../contexts/PermissionsContext";
import PoweredByFooter from "../components/PoweredByFooter";
import DeadlineNotifications from "../components/DeadlineNotifications";
import ThemeToggle from "../components/ThemeToggle";

export default function Dashboard({ user, onLogout, selectedEvent, onBackToEvents }) {
  const navigate = useNavigate();
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [isLeader, setIsLeader] = useState(false);
  const [phaseProgress, setPhaseProgress] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  
  // Ny state til frivilliges egne vagter
  const [myShifts, setMyShifts] = useState([]);
  const [myVolunteerInfo, setMyVolunteerInfo] = useState(null);
  // Mobil-detektion: kombinerer skærmbredde + touch-evne, så laptops i smalle
  // vinduer (fx Emergents preview-ramme) ALTID viser desktop-version, mens
  // rigtige tablets/mobiler stadig får den forenklede mobil-visning.
  const detectMobile = () => {
    const hasTouch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
    return hasTouch && window.innerWidth < 768;
  };
  const [isMobile, setIsMobile] = useState(detectMobile());
  
  // Hent rettigheder fra context
  const { canAccessModule, hasPermission, userLevel, loading: permissionsLoading } = usePermissions();

  const isAdmin = user?.role === 'admin';
  const isGuest = user?.role === 'gæst';
  // "Overordnede lederpositioner": admin, admin_assistant, arrangør får FULD adgang til
  // deres tildelte moduler på både mobil og tablet (som på desktop). Leder/frivillig
  // begrænses fortsat til mobile-optimerede moduler på mobil.
  const isElevatedRole = ['admin', 'admin_assistant', 'arrangør'].includes(user?.role);
  const isArchived = !!selectedEvent?.archived;
  const token = localStorage.getItem('token');
  
  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(detectMobile());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Hent fase-progress for dashboard-widget
  useEffect(() => {
    const fetchPhaseProgress = async () => {
      if (!selectedEvent?.id || !token) return;
      
      try {
        const [phaseRes, progressRes] = await Promise.all([
          axios.get(`${API}/events/${selectedEvent.id}/phase`, {
            headers: { Authorization: `Bearer ${token}` }
          }),
          axios.get(`${API}/events/${selectedEvent.id}/phase/progress`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        setCurrentPhase(phaseRes.data.phase);
        setPhaseProgress(progressRes.data);
      } catch (error) {
        // Fejl er OK - fase-widget er valgfri
        console.log("Fase-data ikke tilgængelig");
      }
    };
    
    fetchPhaseProgress();
  }, [selectedEvent?.id, token]);

  // Tjek om brugeren er leder
  useEffect(() => {
    const checkLeaderStatus = async () => {
      if (!selectedEvent?.id || !token || isAdmin) return;
      
      try {
        // Prøv at hente leder-data - hvis det lykkes, er brugeren leder
        const res = await axios.get(`${API}/events/${selectedEvent.id}/leader/volunteers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsLeader(true);
      } catch (error) {
        // 403 betyder ikke leder - det er ok
        setIsLeader(false);
      }
    };
    
    checkLeaderStatus();
  }, [selectedEvent?.id, token, isAdmin]);

  // Hent frivilliges egne vagter
  useEffect(() => {
    const fetchMyShifts = async () => {
      if (!selectedEvent?.id || !token) return;
      
      try {
        // Hent frivillig-data for den aktuelle bruger
        const volRes = await axios.get(`${API}/events/${selectedEvent.id}/volunteers/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const volunteer = volRes.data;
        setMyVolunteerInfo(volunteer);
        
        if (volunteer?.shift_ids && volunteer.shift_ids.length > 0) {
          // Hent alle vagter for eventet
          const shiftsRes = await axios.get(`${API}/events/${selectedEvent.id}/shifts`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const allShifts = shiftsRes.data;
          
          // Filtrer til kun brugerens vagter
          const userShifts = allShifts.filter(s => volunteer.shift_ids.includes(s.id));
          
          // Sortér efter dato og tid
          userShifts.sort((a, b) => {
            const dateCompare = (a.date || '').localeCompare(b.date || '');
            if (dateCompare !== 0) return dateCompare;
            return (a.start_time || '').localeCompare(b.start_time || '');
          });
          
          setMyShifts(userShifts);
        }
      } catch (error) {
        console.log("Kunne ikke hente vagter:", error.message);
      }
    };
    
    fetchMyShifts();
  }, [selectedEvent?.id, token]);

  const closeConfirm = () => {
    setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
  };

  const getPhaseLabel = (phase) => {
    const labels = {
      'planning': 'Planlægning',
      'setup': 'Opbygning',
      'operation': 'Afvikling',
      'teardown': 'Nedpakning'
    };
    return labels[phase] || phase;
  };

  const getRoleLabel = (role) => {
    const labels = {
      'admin': 'Administrator',
      'heart_leader': '❤️ Heart Leader',
      'chauffør': 'Chauffør',
      'sweeper': 'Sweeper',
      'medarbejder': 'Medarbejder'
    };
    return labels[role] || role;
  };

  // Mapping fra modul-path til guide-sektion
  const guideMap = {
    '/ressourcehandtering': 'kort',
    '/frivillige': 'stamdata',
    '/vagtplaner': 'stamdata',
    '/analytics': 'analyse',
    '/kalender': 'stamdata',
    '/materiel': 'stamdata',
    '/lager': 'stamdata',
    '/beskeder': 'stamdata',
    '/vigtige-info': 'stamdata',
    '/stamdata': 'stamdata',
    '/notifikationer': 'stamdata'
  };

  const modules = [
    {
      title: "Oversigtskort",
      description: "Kort med zoner og ressourceøer",
      icon: Map,
      path: "/ressourcehandtering",
      testId: "nav-resource-map",
      permissionKey: "modul_kort",
      mobileVisible: true,
      guestVisible: true
    },
    {
      title: "Frivillige",
      description: "Oversigt over alle frivillige",
      icon: Heart,
      path: "/frivillige",
      testId: "nav-volunteers",
      permissionKey: "modul_frivillige"
    },
    {
      title: "Vagtplan",
      description: "Se hvem der er på arbejde",
      icon: Users,
      path: "/vagtplaner",
      testId: "nav-shifts",
      permissionKey: "modul_vagtplan"
    },
    {
      title: "Analytics",
      description: "Rapportering og statistik",
      icon: Calendar,
      path: "/analytics",
      testId: "nav-analytics",
      permissionKey: "modul_analyse",
      // Gæster må kun se Analytics hvis admin har slået det til i deres visible_layers
      guestVisible: !!user?.visible_layers?.analytics_module,
      // Gæster med permission skal også kunne åbne Analytics på telefon
      mobileVisible: !!user?.visible_layers?.analytics_module,
    },
    {
      title: "Kalender",
      description: "Planlægning og tidslinjer",
      icon: Calendar,
      path: "/kalender",
      testId: "nav-calendar",
      permissionKey: "modul_kalender"
    },
    {
      title: "Materiel",
      description: "Lager og udstyr",
      icon: Package,
      path: "/materiel",
      testId: "nav-materials",
      permissionKey: "modul_materiel"
    },
    {
      title: "Lager",
      description: "Nedpakning og status",
      icon: Warehouse,
      path: "/lager",
      testId: "nav-lager",
      permissionKey: "modul_materiel"
    },
    {
      title: "Beskeder",
      description: "Kommunikation mellem hold",
      icon: MessageSquare,
      path: "/beskeder",
      testId: "nav-beskeder",
      permissionKey: "modul_beskeder",
      mobileVisible: true
    },
    {
      title: "Vigtige Informationer",
      description: "Fraktioner, instruktioner og evaluering",
      icon: FileText,
      path: "/vigtige-info",
      testId: "nav-vigtige-info",
      permissionKey: "modul_vigtige_info",
      mobileVisible: true
    },
    {
      title: "Live Monitor",
      description: "Kontrolrumskort med live status",
      icon: Activity,
      path: "/live-monitor",
      testId: "nav-live-monitor",
      adminOnly: true
    }
  ];

  // Filtrer moduler baseret på rettigheder + mobil + gæst
  const visibleModules = modules.filter(mod => {
    if (mod.adminOnly) return isAdmin; // Kun admin (fx Live Monitor)
    if (isGuest) return true; // Gæster ser alle moduler
    if (!mod.permissionKey) return true;
    if (isMobile && !mod.mobileVisible) return false;
    return hasPermission(mod.permissionKey);
  });

  // Leder-modul (kun synligt for ledere)
  const leaderModules = [
    {
      title: "Leder-oversigt",
      description: "Administrer dit hold",
      icon: Crown,
      path: "/leder",
      testId: "nav-leder"
    }
  ];

  // Admin-only modules (vises separat i bunden)
  const adminModules = [
    {
      title: "Stamdata",
      description: "Frivillige, teams og grunddata",
      icon: FileSpreadsheet,
      path: "/stamdata",
      testId: "nav-stamdata",
      permissionKey: "modul_stamdata"
    }
  ];

  // Filtrer admin-moduler baseret på rettigheder
  const visibleAdminModules = adminModules.filter(mod => {
    if (!mod.permissionKey) return true;
    return hasPermission(mod.permissionKey);
  });

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Bekræftelses-dialog */}
      <ConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
      
      <header className="border-b border-border bg-card sticky top-0 z-20">
        <div className="container mx-auto px-3 sm:px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-4">
            {!user?.quick_checkin_session && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onBackToEvents}
                className="mr-1 sm:mr-2"
                data-testid="back-to-events-btn"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            )}
            <div>
              <h1 className="text-lg sm:text-2xl font-bold font-heading uppercase tracking-tight">
                {selectedEvent?.name || "Resovia"}
              </h1>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider hidden sm:block">
                Festival Logistics Dashboard
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="text-right hidden sm:block">
              <button 
                onClick={() => navigate('/profil')} 
                className="text-right hover:opacity-70 transition-opacity"
                title="Min profil"
                data-testid="profile-link"
              >
                <p className="text-sm font-medium" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-muted-foreground font-mono uppercase" data-testid="user-role">
                  {getRoleLabel(user?.role)}
                </p>
              </button>
            </div>
            {/* Event-logo i højre side - synlig og i god størrelse */}
            {selectedEvent?.logo_url && (
              <img 
                src={`${API.replace('/api', '')}${selectedEvent.logo_url}`} 
                alt={selectedEvent.name} 
                className="h-10 sm:h-12 w-auto object-contain rounded"
                data-testid="topbar-event-logo"
              />
            )}
            {/* Deadline notifikationer - kun for admin og administratorassistent */}
            {(isAdmin || user?.role === 'assisterende_admin') && (
              <DeadlineNotifications eventId={selectedEvent?.id} />
            )}
            {isAdmin && (
              <>
                <Button
                  data-testid="idebank-btn"
                  onClick={() => navigate('/idebank')}
                  variant="outline"
                  size="sm"
                  title="Idébank"
                >
                  <Sparkles className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              data-testid="guide-btn"
              onClick={() => navigate('/brugsvejledning')}
              variant="outline"
              size="sm"
              title="Brugsvejledning"
            >
              <HelpCircle className="h-4 w-4" />
            </Button>
            <ThemeToggle />
            <Button
              data-testid="logout-button"
              onClick={onLogout}
              variant="ghost"
              size="sm"
              className="hover:bg-accent hover:text-accent-foreground h-9 px-4 rounded-md"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Arkiveret banner */}
        {isArchived && (
          <div className="mb-6 flex items-center gap-3 bg-amber-500/10 border border-amber-500/30 text-amber-200 rounded-lg px-4 py-3" data-testid="archived-banner">
            <Archive className="h-5 w-5 shrink-0 text-amber-400" />
            <div className="flex-1">
              <p className="font-semibold text-sm">Arkiveret event &mdash; read-only</p>
              <p className="text-xs text-amber-300/70">Ændringer er deaktiveret. Gå tilbage og genaktiver eventet for at redigere.</p>
            </div>
            <Button variant="outline" size="sm" onClick={onBackToEvents} className="border-amber-500/30 text-amber-300 hover:bg-amber-500/20 shrink-0" data-testid="back-from-archived-btn">
              <ChevronLeft className="h-4 w-4 mr-1" /> Tilbage
            </Button>
          </div>
        )}
        {/* Mine vagter - vises for ikke-admin brugere */}
        {!isAdmin && (
          <div className="mb-6 bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-xl p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-primary/20 p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h2 className="text-base sm:text-lg font-semibold">
                  Dine Vagter
                </h2>
                {myVolunteerInfo && (
                  <p className="text-xs text-muted-foreground">
                    {myVolunteerInfo.role_name && <span className="text-primary font-medium">{myVolunteerInfo.role_name}</span>}
                    {myVolunteerInfo.area && <span> • {myVolunteerInfo.area}</span>}
                    {myVolunteerInfo.work_zone && <span> • {myVolunteerInfo.work_zone}</span>}
                  </p>
                )}
              </div>
            </div>
            
            {myShifts.length > 0 ? (
              <div className="space-y-2">
                {myShifts.map((shift) => {
                  const formatDate = (dateStr) => {
                    if (!dateStr) return 'Ukendt dato';
                    const date = new Date(dateStr);
                    const days = ['Søn', 'Man', 'Tirs', 'Ons', 'Tors', 'Fre', 'Lør'];
                    return `${days[date.getDay()]} d. ${date.getDate()}/${date.getMonth() + 1}`;
                  };
                  
                  // Check for individuel vagttid override
                  const override = myVolunteerInfo?.shift_time_overrides?.[shift.id];
                  const startTime = override?.start_time || shift.start_time || '??:??';
                  const endTime = override?.end_time || shift.end_time || '??:??';
                  
                  return (
                    <div 
                      key={shift.id}
                      className="bg-card/50 border border-border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4"
                    >
                      {/* Dato og tid */}
                      <div className="flex items-center gap-3 sm:min-w-[140px]">
                        <span className="text-sm font-medium text-muted-foreground w-16">
                          {formatDate(shift.date)}
                        </span>
                        <span className="text-sm font-bold text-primary">
                          {startTime}-{endTime}
                        </span>
                      </div>
                      
                      {/* Rolle og lokation */}
                      <div className="flex-1 text-sm">
                        <span className="font-medium">
                          {myVolunteerInfo?.role_name || myVolunteerInfo?.role || shift.name || 'Vagt'}
                        </span>
                        {(myVolunteerInfo?.area || shift.area) && (
                          <span className="text-muted-foreground">
                            {' '}på {myVolunteerInfo?.area || shift.area}
                          </span>
                        )}
                        {(myVolunteerInfo?.work_zone || shift.zone) && (
                          <span className="text-muted-foreground text-xs">
                            {' '}• {myVolunteerInfo?.work_zone || shift.zone}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Du har ingen vagter tildelt endnu.
              </p>
            )}
          </div>
        )}

        <div className="mb-6">
          <h2 className="text-lg sm:text-xl font-semibold font-heading uppercase mb-1">
            Vælg Modul
          </h2>
          <p className="text-muted-foreground text-sm">
            Administrer {selectedEvent?.name || "festival"}-logistik
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {visibleModules
            .filter(module => !module.adminOnly || isAdmin)
            .filter(module => !isMobile || isElevatedRole || module.mobileVisible)
            .map((module) => {
            const Icon = module.icon;
            const guestDisabled = isGuest && !module.guestVisible;
            return (
              <button
                key={module.path}
                data-testid={module.testId}
                onClick={() => !guestDisabled && navigate(module.path)}
                className={`relative bg-card text-card-foreground border border-border shadow-sm rounded-md overflow-hidden transition-all p-6 text-left group ${guestDisabled ? 'opacity-40 cursor-not-allowed' : 'hover:border-primary/50 cursor-pointer active:scale-95'}`}
                disabled={guestDisabled}
              >
                <a
                  href="/brugsvejledning"
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-muted/50 text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                  title="Hjælp"
                >
                  <HelpCircle className="h-3.5 w-3.5" />
                </a>
                <Icon className="h-8 w-8 text-primary mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-semibold font-heading uppercase mb-2">
                  {module.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {module.description}
                </p>
              </button>
            );
          })}
        </div>

        {/* AI-Evaluering — for ledere og arrangører/admin (også på mobil) */}
        {(isLeader || isAdmin || ['admin_assistant', 'arrangør'].includes(user?.role)) && (
          <div className="mt-8 pt-6 border-t border-primary/30">
            <p className="text-xs text-primary uppercase tracking-wider mb-3 flex items-center gap-2">
              <Sparkles className="h-3 w-3" />
              Evaluering
            </p>
            <button
              data-testid="nav-evaluering"
              onClick={() => navigate('/evaluering')}
              className="w-full md:w-auto bg-primary/5 text-card-foreground border border-primary/30 rounded-md overflow-hidden hover:border-primary/60 transition-all cursor-pointer px-5 py-4 text-left group active:scale-95 flex items-center gap-4"
            >
              <Sparkles className="h-6 w-6 text-primary group-hover:scale-110 transition-transform" />
              <div>
                <h3 className="text-base font-semibold font-heading uppercase">Evaluering</h3>
                <p className="text-xs text-muted-foreground">Indtal eller skriv — AI sorterer det i emner</p>
              </div>
            </button>
          </div>
        )}


        {/* Leder-moduler - kun synlige for ledere, skjult på mobil */}
        {isLeader && !isAdmin && !isMobile && (
          <div className="mt-8 pt-6 border-t border-yellow-500/30">
            <p className="text-xs text-yellow-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <Crown className="h-3 w-3" />
              Leder-funktioner
            </p>
            <div className="flex gap-4">
              {leaderModules.map((module) => {
                const Icon = module.icon;
                return (
                  <button
                    key={module.path}
                    data-testid={module.testId}
                    onClick={() => navigate(module.path)}
                    className="bg-yellow-500/10 text-card-foreground border border-yellow-500/30 rounded-md overflow-hidden hover:border-yellow-500/50 transition-all cursor-pointer px-4 py-3 text-left group active:scale-95 flex items-center gap-3"
                  >
                    <Icon className="h-5 w-5 text-yellow-500 group-hover:scale-110 transition-transform" />
                    <div>
                      <h3 className="text-sm font-medium">{module.title}</h3>
                      <p className="text-xs text-muted-foreground">{module.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Admin Datagrundlag - vises på desktop for alle med adgang, og på mobil/tablet for admin/admin_assistant/arrangør */}
        {(isElevatedRole || !isMobile) && (isAdmin || hasPermission('modul_stamdata')) && visibleAdminModules.length > 0 && (
          <div className="mt-12 pt-6 border-t border-border/30">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-3">Administration</p>
            <div className="flex gap-4">
              {visibleAdminModules.map((module) => {
                const Icon = module.icon;
                return (
                  <button
                    key={module.path}
                    data-testid={module.testId}
                    onClick={() => navigate(module.path)}
                    className="bg-card/50 text-card-foreground border border-border/50 rounded-md overflow-hidden hover:border-primary/30 transition-all cursor-pointer px-4 py-3 text-left group active:scale-95 flex items-center gap-3"
                  >
                    <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    <div>
                      <h3 className="text-sm font-medium">{module.title}</h3>
                      <p className="text-xs text-muted-foreground">{module.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Fase-widget - Vises i opbygning/nedpakning faserne */}
        {(currentPhase === 'setup' || currentPhase === 'teardown') && phaseProgress && (
          <div className="mt-8 pt-6 border-t border-primary/30">
            <div className="bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/30 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    currentPhase === 'setup' ? 'bg-blue-500/20' : 'bg-orange-500/20'
                  }`}>
                    <Settings className={`h-5 w-5 ${
                      currentPhase === 'setup' ? 'text-blue-500' : 'text-orange-500'
                    }`} />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg uppercase font-mono">
                      {getPhaseLabel(currentPhase)}
                    </h3>
                    <p className="text-xs text-zinc-500">
                      {phaseProgress.green_count} af {phaseProgress.total_points} punkter færdige
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/setup-log')}
                  data-testid="setup-log-btn"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Se historik
                </Button>
              </div>
              
              {/* Progress bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="font-mono">{phaseProgress.progress_percentage}% færdig</span>
                  <div className="flex gap-3">
                    <span className="text-red-500">🔴 {phaseProgress.resource_points?.by_status?.red || 0}</span>
                    <span className="text-yellow-500">🟡 {phaseProgress.resource_points?.by_status?.yellow || 0}</span>
                    <span className="text-green-500">🟢 {phaseProgress.resource_points?.by_status?.green || 0}</span>
                  </div>
                </div>
                <div className="h-3 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      phaseProgress.progress_percentage === 100 
                        ? 'bg-green-500' 
                        : currentPhase === 'setup' 
                          ? 'bg-blue-500' 
                          : 'bg-orange-500'
                    }`}
                    style={{ width: `${phaseProgress.progress_percentage}%` }}
                  />
                </div>
              </div>

              {/* Split pr. type: ressourceøer / ressourcespande / maxicontainere */}
              {phaseProgress.by_type && (
                <div className="mt-4 grid gap-2" data-testid="phase-by-type">
                  {[
                    { key: 'ressourceoer', label: 'Ressourceøer' },
                    { key: 'ressourcespande', label: 'Ressourcespande' },
                    { key: 'maxicontainere', label: 'Maxicontainere' },
                  ].filter(t => (phaseProgress.by_type[t.key]?.total || 0) > 0).map(t => {
                    const d = phaseProgress.by_type[t.key];
                    return (
                      <div key={t.key} className="flex items-center gap-3" data-testid={`phase-type-${t.key}`}>
                        <span className="text-xs font-mono w-32 text-zinc-300 flex-shrink-0">{t.label}</span>
                        <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
                          <div className="h-full bg-green-500 transition-all duration-500" style={{ width: `${d.progress_percentage}%` }} />
                        </div>
                        <span className="text-xs font-mono text-zinc-400 w-24 text-right flex-shrink-0">{d.green_count}/{d.total} ({d.progress_percentage}%)</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {phaseProgress.progress_percentage === 100 && (
                <div className="mt-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-center">
                  <p className="text-green-400 font-bold">
                    🎉 {getPhaseLabel(currentPhase)} er 100% færdig!
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Platform Settings link (kun topadmin) + Powered by */}
        <div className="mt-8 space-y-4">
          {isAdmin && !isMobile && (
            <div className="flex justify-center">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate('/platform-settings')}
                className="gap-2 text-muted-foreground"
                data-testid="platform-settings-link"
              >
                <Settings className="h-4 w-4" />
                Platform-indstillinger
              </Button>
            </div>
          )}
          <PoweredByFooter />
        </div>
      </main>
    </div>
  );
}
